<template>
  <header class="bg-white text-gray-900 flex items-center justify-between px-4 h-[60px] border-b border-gray-200 sticky top-0 z-[1000] transition-colors duration-300">
    <!-- Left: Logo and Brand -->
    <div class="flex items-center">
      <!-- Mobile Menu Toggle -->
      <button 
        @click="uiStore.toggleMobileMenu()"
        class="md:hidden p-2 mr-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
        aria-label="Toggle Menu"
      >
        <Menu v-if="!uiStore.isMobileMenuOpen" class="w-6 h-6" />
        <X v-else class="w-6 h-6" />
      </button>

      <div class="flex items-center gap-2 cursor-pointer group" @click="router.push({ name: 'Home' })">
        <div class="flex items-center gap-2">
          <div class="w-10 h-10 bg-[#DC2626] rounded-lg flex items-center justify-center">
            <img src="@/assets/logo.png" width="35" height="35" alt="Logo" class="rounded-sm">
          </div>
          <h1 class="text-2xl font-bold text-[#DC2626]">Secrable</h1>
        </div>
      </div>
    </div>

    <!-- Right: Actions and User Menu -->
    <div class="flex items-center gap-2 sm:gap-4">

      

      <router-link
            to="/GetApp"
            class="ml-4 px-6 py-2.5 bg-red-600 text-white text-sm font-black rounded-xl hover:bg-red-500 transition-colors shadow-[0_0_15px_rgba(239,68,68,0.2)] hidden lg:block"
          >
            Get App
          </router-link>

          <!-- User Avatar / Profile Link -->
          <router-link
            v-if="authStore.isAuthenticated"
            to="/UserInfo"
            class="ml-2 sm:ml-4 flex items-center gap-2 group"
          >
            <div class="w-9 h-9 rounded-full overflow-hidden border-2 border-gray-100 shadow-sm group-hover:border-red-500 transition-all duration-300">
              <img 
                v-if="authStore.profile?.profile_picture" 
                :src="authStore.profile.profile_picture" 
                alt="Profile"
                class="w-full h-full object-cover"
              >
              <div v-else class="w-full h-full bg-red-600 flex items-center justify-center text-white font-bold text-xs">
                {{ authStore.user?.username?.charAt(0).toUpperCase() || 'U' }}
              </div>
            </div>
            <span class="text-sm font-bold text-gray-700 group-hover:text-red-600 transition-colors hidden md:block">
              {{ authStore.user?.username }}
            </span>
          </router-link>
      <!-- Utility Icons -->
      <div v-if="authStore.isAuthenticated" class="flex items-center gap-1">
        <button 
          @click="router.push({ name: 'notification' })"
          class="p-2 text-gray-700 rounded-full hover:bg-gray-100 transition-colors relative" 
          title="Notifications"
        >
          <Bell class="w-6 h-6" />
          <span v-if="notificationStore.unreadCount > 0" class="absolute top-1 right-1 w-4 h-4 bg-blue-600 text-white text-[10px] rounded-full flex items-center justify-center font-bold">
            {{ notificationStore.unreadCount }}
          </span>
        </button>
      </div>

      <!-- Guest Login -->
      <button 
        v-if="!authStore.isAuthenticated"
        @click="router.push({ name: 'authSelect' })"
        class="bg-blue-900 text-white rounded-full px-4 py-2 font-bold text-sm hover:bg-blue-600 transition-all active:scale-95 shadow-lg shadow-blue-900/10" 
      >
        Log In
      </button>

      <!-- User Menu Toggle (Authenticated Only) -->
      <div v-else class="relative" ref="userMenuRef">
        <button 
          @click="toggleUserMenu"
          class="text-gray-500 hover:text-blue-600 p-2 rounded-full hover:bg-gray-100 transition-all" 
          title="Options"
        >
          <MoreVertical class="w-6 h-6" />
        </button>

        <!-- Dropdown Window -->
        <div 
          v-if="isUserMenuOpen"
          class="absolute right-0 top-[120%] w-56 bg-white border border-gray-200 rounded-xl shadow-2xl py-2 z-[1100] transform transition-all duration-200 origin-top-right"
        >
          <div class="px-4 py-2 text-xs font-bold text-gray-400 uppercase tracking-wider">Account</div>
          <div class="my-1 border-t border-gray-100"></div>
          <button 
            @click="handleLogout"
            class="w-full flex items-center gap-3 px-4 py-3 hover:bg-red-50 transition-colors text-red-600"
          >
            <LogOut class="w-5 h-5" />
            <span class="text-sm font-medium">Log Out</span>
          </button>
            <router-link
              to="/UserForm"
              class="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors text-gray-700 bg-red-600 text-white tex-sm rounded-xl hover:bg-red-500 transition-colors shadow-[0_0_15px_rgba(239,68,68,0.2)]"
            >
              <div class="w-5 h-5 flex items-center justify-center">
                <i class="fa-solid fa-user-gear text-sm"></i>
              </div>
              <span class="text-sm font-medium">Send Profile</span>
            </router-link>
        </div>
      </div>
    </div>
  </header>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';
import { useUiStore } from '@/Stores/ui';
import { useNotificationStore } from '@/Stores/notification';
import { useAuthStore } from '@/Stores/Auth';
import { 
  Menu, 
  X, 
  Bell, 
  MoreVertical, 
  LogOut 
} from 'lucide-vue-next';

const uiStore = useUiStore();
const notificationStore = useNotificationStore();
const authStore = useAuthStore();
const router = useRouter();
const isUserMenuOpen = ref(false);
const userMenuRef = ref(null);

const toggleUserMenu = () => {
    isUserMenuOpen.value = !isUserMenuOpen.value;
};

const handleClickOutside = (event) => {
    if (userMenuRef.value && !userMenuRef.value.contains(event.target)) {
        isUserMenuOpen.value = false;
    }
};

const handleLogout = () => {
    authStore.logout();
    isUserMenuOpen.value = false;
    router.push({ name: 'Home' });
};

onMounted(() => {
    window.addEventListener('click', handleClickOutside);
    if (authStore.isAuthenticated) {
        notificationStore.fetchNotifications();
    }
});

onUnmounted(() => {
    window.removeEventListener('click', handleClickOutside);
});
</script>

<script>
export default {
    name: "HeaderComponent"
}
</script>
